import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import api from '@/lib/api';
import { EphySearchInput, CibleSuggestInput, EphyProduitCard } from '@/components/EphyComponents';
import {
  FlaskConical,
  AlertTriangle,
  CheckCircle2,
  Package,
  Plus,
  Search,
  Calendar,
  Droplets,
  FileText,
  Download,
  Filter,
  RefreshCw,
  Loader2,
  ArrowUpDown,
  TrendingUp,
  Leaf,
  Bug,
  Sprout,
  Eye,
  Database
} from 'lucide-react';

const RegistrePhytoPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [produits, setProduits] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [conformite, setConformite] = useState(null);
  const [campagne, setCampagne] = useState(new Date().getFullYear());
  
  // Filters
  const [searchProduit, setSearchProduit] = useState('');
  const [filterTypeProduit, setFilterTypeProduit] = useState('all');
  
  // Dialogs
  const [showNewProduit, setShowNewProduit] = useState(false);
  const [showNewIntervention, setShowNewIntervention] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showEphyDetail, setShowEphyDetail] = useState(false);
  const [selectedEphyAmm, setSelectedEphyAmm] = useState(null);
  
  // EPHY state
  const [selectedEphyProduit, setSelectedEphyProduit] = useState(null);
  
  // Forms
  const [newProduit, setNewProduit] = useState({
    nom_commercial: '',
    numero_amm: '',
    substance_active: '',
    type_produit: 'herbicide',
    formulation: '',
    fabricant: '',
    dose_max_ha: '',
    unite_dose: 'L/ha',
    delai_reentree: '',
    znt_aquatique: '',
    quantite_stock: 0,
    prix_unitaire: '',
    date_peremption: '',
    cultures_homologuees: [],
    // Nouveaux champs EPHY
    phrases_danger: [],
    epi_requis: [],
    znt_riverains: ''
  });
  
  const [newIntervention, setNewIntervention] = useState({
    parcelle_id: '',
    produit_id: '',
    date_intervention: new Date().toISOString().slice(0, 10),
    operateur_nom: '',
    materiel_nom: '',
    dose_appliquee: '',
    unite_dose: 'L/ha',
    surface_traitee: '',
    volume_bouillie: '',
    temperature: '',
    vent_kmh: '',
    hygrometrie: '',
    stade_culture: '',
    cible: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, produitsRes, interventionsRes, conformiteRes, parcellesRes] = await Promise.all([
        api.get(`/registre-phyto/stats?campagne=${campagne}`).catch(() => ({ data: null })),
        api.get('/registre-phyto/produits').catch(() => ({ data: { produits: [] } })),
        api.get(`/registre-phyto/interventions?campagne=${campagne}`).catch(() => ({ data: { interventions: [] } })),
        api.get(`/registre-phyto/conformite?campagne=${campagne}`).catch(() => ({ data: null })),
        api.get('/parcellaire/parcelles').catch(() => ({ data: { parcelles: [] } }))
      ]);
      
      setStats(statsRes.data);
      setProduits(produitsRes.data?.produits || []);
      setInterventions(interventionsRes.data?.interventions || []);
      setConformite(conformiteRes.data);
      setParcelles(parcellesRes.data?.parcelles || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  }, [campagne]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fonction pour remplir le formulaire depuis un produit EPHY sélectionné
  const handleEphySelect = (ephyProduit) => {
    setSelectedEphyProduit(ephyProduit);
    
    // Remplir automatiquement le formulaire avec les données EPHY
    setNewProduit(prev => ({
      ...prev,
      nom_commercial: ephyProduit.nom_commercial || '',
      numero_amm: ephyProduit.numero_amm || '',
      substance_active: ephyProduit.substances_actives?.map(s => s.nom).join(', ') || '',
      type_produit: ephyProduit.fonction || 'herbicide',
      formulation: ephyProduit.formulation || '',
      fabricant: ephyProduit.firme || '',
      // Les usages ont les doses max par culture
      dose_max_ha: ephyProduit.usages?.[0]?.dose_max || '',
      unite_dose: ephyProduit.usages?.[0]?.unite_dose || 'L/ha',
      delai_reentree: ephyProduit.conditions_emploi?.delai_reentree || '',
      znt_aquatique: ephyProduit.conditions_emploi?.znt_aquatique || '',
      znt_riverains: ephyProduit.conditions_emploi?.znt_riverains || '',
      phrases_danger: ephyProduit.phrases_danger || [],
      epi_requis: ephyProduit.conditions_emploi?.epi_requis || []
    }));
    
    toast.success(`Données EPHY importées pour ${ephyProduit.nom_commercial}`);
  };

  const createProduit = async () => {
    try {
      await api.post('/registre-phyto/produits', {
        ...newProduit,
        dose_max_ha: newProduit.dose_max_ha ? parseFloat(newProduit.dose_max_ha) : null,
        delai_reentree: newProduit.delai_reentree ? parseInt(newProduit.delai_reentree) : null,
        znt_aquatique: newProduit.znt_aquatique ? parseInt(newProduit.znt_aquatique) : null,
        znt_riverains: newProduit.znt_riverains ? parseInt(newProduit.znt_riverains) : null,
        quantite_stock: parseFloat(newProduit.quantite_stock) || 0,
        prix_unitaire: newProduit.prix_unitaire ? parseFloat(newProduit.prix_unitaire) : null
      });
      toast.success('Produit ajouté au registre');
      setShowNewProduit(false);
      setSelectedEphyProduit(null);
      setNewProduit({
        nom_commercial: '', numero_amm: '', substance_active: '', type_produit: 'herbicide',
        formulation: '', fabricant: '', dose_max_ha: '', unite_dose: 'L/ha',
        delai_reentree: '', znt_aquatique: '', znt_riverains: '', quantite_stock: 0, 
        prix_unitaire: '', date_peremption: '', cultures_homologuees: [],
        phrases_danger: [], epi_requis: []
      });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du produit');
    }
  };

  const createIntervention = async () => {
    if (!newIntervention.parcelle_id || !newIntervention.produit_id || !newIntervention.dose_appliquee) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    try {
      const result = await api.post('/registre-phyto/interventions', {
        ...newIntervention,
        dose_appliquee: parseFloat(newIntervention.dose_appliquee),
        surface_traitee: parseFloat(newIntervention.surface_traitee) || 0,
        volume_bouillie: newIntervention.volume_bouillie ? parseFloat(newIntervention.volume_bouillie) : null,
        temperature: newIntervention.temperature ? parseFloat(newIntervention.temperature) : null,
        vent_kmh: newIntervention.vent_kmh ? parseFloat(newIntervention.vent_kmh) : null,
        hygrometrie: newIntervention.hygrometrie ? parseFloat(newIntervention.hygrometrie) : null
      });
      
      if (result.data?.dose_respectee === false) {
        toast.warning('Attention: Dose maximale dépassée !', { duration: 5000 });
      } else {
        toast.success('Intervention enregistrée au registre');
      }
      
      setShowNewIntervention(false);
      setNewIntervention({
        parcelle_id: '', produit_id: '', date_intervention: new Date().toISOString().slice(0, 10),
        operateur_nom: '', materiel_nom: '', dose_appliquee: '', unite_dose: 'L/ha',
        surface_traitee: '', volume_bouillie: '', temperature: '', vent_kmh: '',
        hygrometrie: '', stade_culture: '', cible: '', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const exportRegistre = async () => {
    try {
      const response = await api.get(`/registre-phyto/export/registre?campagne=${campagne}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registre_phyto_${campagne}.json`;
      a.click();
      toast.success('Registre exporté');
      setShowExport(false);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const filteredProduits = produits.filter(p => {
    if (searchProduit && !p.nom_commercial?.toLowerCase().includes(searchProduit.toLowerCase()) &&
        !p.substance_active?.toLowerCase().includes(searchProduit.toLowerCase())) {
      return false;
    }
    if (filterTypeProduit !== 'all' && p.type_produit !== filterTypeProduit) {
      return false;
    }
    return true;
  });

  const getTypeProduitIcon = (type) => {
    switch (type) {
      case 'herbicide': return <Leaf className="w-4 h-4 text-green-600" />;
      case 'fongicide': return <FlaskConical className="w-4 h-4 text-purple-600" />;
      case 'insecticide': return <Bug className="w-4 h-4 text-red-600" />;
      default: return <Droplets className="w-4 h-4 text-blue-600" />;
    }
  };

  const getTypeProduitColor = (type) => {
    switch (type) {
      case 'herbicide': return 'bg-green-100 text-green-800';
      case 'fongicide': return 'bg-purple-100 text-purple-800';
      case 'insecticide': return 'bg-red-100 text-red-800';
      case 'adjuvant': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="registre-phyto-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="w-7 h-7 text-green-600" />
            Registre Phytosanitaire
          </h1>
          <p className="text-muted-foreground">
            Traçabilité des produits et interventions - Campagne {campagne}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={campagne.toString()} onValueChange={(v) => setCampagne(parseInt(v))}>
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2026, 2025, 2024, 2023].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowExport(true)}>
            <Download className="w-4 h-4 mr-2" />
            Exporter
          </Button>
          <Button onClick={loadData} variant="ghost" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Tableau de bord
          </TabsTrigger>
          <TabsTrigger value="produits" className="gap-2">
            <Package className="w-4 h-4" />
            Produits ({produits.length})
          </TabsTrigger>
          <TabsTrigger value="interventions" className="gap-2">
            <Sprout className="w-4 h-4" />
            Interventions ({interventions.length})
          </TabsTrigger>
          <TabsTrigger value="conformite" className="gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Conformité
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.nb_produits || 0}</p>
                    <p className="text-sm text-muted-foreground">Produits</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-green-100">
                    <Sprout className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.nb_interventions || 0}</p>
                    <p className="text-sm text-muted-foreground">Interventions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-amber-100">
                    <Leaf className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.surface_traitee?.toFixed(1) || 0} ha</p>
                    <p className="text-sm text-muted-foreground">Surface traitée</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={conformite?.taux_conformite < 100 ? 'border-red-200' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-full ${conformite?.taux_conformite === 100 ? 'bg-green-100' : 'bg-red-100'}`}>
                    {conformite?.taux_conformite === 100 ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{conformite?.taux_conformite || 100}%</p>
                    <p className="text-sm text-muted-foreground">Conformité</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stock faible */}
          {stats?.stock_faible?.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Stocks faibles ({stats.stock_faible.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {stats.stock_faible.map(p => (
                    <Badge key={p.id} variant="outline" className="border-amber-400">
                      {p.nom_commercial}: {p.quantite_stock} {p.unite_dose?.split('/')[0]}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top produits */}
          {stats?.top_produits?.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Top 5 produits utilisés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.top_produits.slice(0, 5).map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="font-medium">{p.nom}</span>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{p.nb} applications</span>
                        <Badge variant="secondary">{p.quantite?.toFixed(1)} L/kg</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Produits Tab */}
        <TabsContent value="produits" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un produit..."
                value={searchProduit}
                onChange={(e) => setSearchProduit(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterTypeProduit} onValueChange={setFilterTypeProduit}>
              <SelectTrigger className="w-44">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="herbicide">Herbicides</SelectItem>
                <SelectItem value="fongicide">Fongicides</SelectItem>
                <SelectItem value="insecticide">Insecticides</SelectItem>
                <SelectItem value="adjuvant">Adjuvants</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setShowNewProduit(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau produit
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProduits.map(produit => (
              <Card key={produit.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getTypeProduitIcon(produit.type_produit)}
                      <span className="font-semibold">{produit.nom_commercial}</span>
                    </div>
                    <Badge className={getTypeProduitColor(produit.type_produit)}>
                      {produit.type_produit}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {produit.numero_amm && (
                      <div className="flex items-center gap-2">
                        <FileText className="w-3 h-3 text-muted-foreground" />
                        <span className="text-muted-foreground">AMM:</span>
                        <span className="font-mono">{produit.numero_amm}</span>
                      </div>
                    )}
                    {produit.substance_active && (
                      <div className="text-muted-foreground">
                        {produit.substance_active}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        <span className={produit.quantite_stock < 5 ? 'text-red-600 font-semibold' : ''}>
                          {produit.quantite_stock} {produit.unite_dose?.split('/')[0]}
                        </span>
                      </div>
                      {produit.dose_max_ha && (
                        <span className="text-muted-foreground">
                          Max: {produit.dose_max_ha} {produit.unite_dose}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {filteredProduits.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun produit trouvé</p>
              <Button variant="link" onClick={() => setShowNewProduit(true)}>
                Ajouter un produit
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Interventions Tab */}
        <TabsContent value="interventions" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {interventions.length} interventions enregistrées en {campagne}
            </p>
            <Button onClick={() => setShowNewIntervention(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle intervention
            </Button>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {interventions.map(interv => (
                <Card key={interv.id} className={!interv.dose_respectee ? 'border-red-300 bg-red-50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{interv.date_intervention}</Badge>
                        <span className="font-semibold">{interv.produit_nom}</span>
                        {!interv.dose_respectee && (
                          <Badge variant="destructive" className="text-xs">
                            Dose dépassée
                          </Badge>
                        )}
                      </div>
                      <Badge className={getTypeProduitColor(interv.produit_type)}>
                        {interv.produit_type}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Parcelle</p>
                        <p className="font-medium">{interv.parcelle_nom || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Surface</p>
                        <p className="font-medium">{interv.surface_traitee} ha</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Dose</p>
                        <p className={`font-medium ${!interv.dose_respectee ? 'text-red-600' : ''}`}>
                          {interv.dose_appliquee} {interv.unite_dose}
                          {interv.dose_max_autorisee && (
                            <span className="text-muted-foreground text-xs ml-1">
                              (max: {interv.dose_max_autorisee})
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Quantité</p>
                        <p className="font-medium">{interv.quantite_utilisee} {interv.unite_dose?.split('/')[0]}</p>
                      </div>
                    </div>
                    {interv.cible && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Cible: {interv.cible}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              {interventions.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Sprout className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune intervention cette campagne</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Conformité Tab */}
        <TabsContent value="conformite" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-6">
                <div className={`p-6 rounded-full ${conformite?.taux_conformite === 100 ? 'bg-green-100' : 'bg-red-100'}`}>
                  {conformite?.taux_conformite === 100 ? (
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-12 h-12 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-4xl font-bold">{conformite?.taux_conformite || 100}%</p>
                  <p className="text-lg text-muted-foreground">Taux de conformité</p>
                  <p className="text-sm">
                    {conformite?.stats?.conformes || 0} conformes / {conformite?.stats?.total_interventions || 0} interventions
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {conformite?.alertes?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Alertes ({conformite.alertes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {conformite.alertes.map((alerte, idx) => (
                    <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="destructive">Dose dépassée</Badge>
                        <span className="text-sm text-muted-foreground">{alerte.date}</span>
                      </div>
                      <p className="font-medium">{alerte.produit}</p>
                      <p className="text-sm text-muted-foreground">Parcelle: {alerte.parcelle}</p>
                      <p className="text-sm text-red-600">{alerte.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(!conformite?.alertes || conformite.alertes.length === 0) && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-600 mb-4" />
                <p className="text-lg font-medium text-green-800">Toutes les interventions sont conformes</p>
                <p className="text-sm text-green-600">Aucune alerte détectée pour cette campagne</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Nouveau Produit avec recherche EPHY */}
      <Dialog open={showNewProduit} onOpenChange={(open) => {
        setShowNewProduit(open);
        if (!open) setSelectedEphyProduit(null);
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              Nouveau produit phytosanitaire
            </DialogTitle>
            <DialogDescription>
              Recherchez dans le catalogue EPHY (ANSES) pour importer automatiquement les données officielles
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Recherche EPHY */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-blue-600" />
                <Label className="text-blue-800 font-medium">Recherche catalogue EPHY</Label>
              </div>
              <EphySearchInput 
                onSelect={handleEphySelect}
                placeholder="Tapez le nom du produit (ex: Stratos, Roundup...)"
              />
              {selectedEphyProduit && (
                <div className="mt-3 flex items-center justify-between bg-white p-2 rounded border border-blue-300">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">{selectedEphyProduit.nom_commercial}</span>
                    <Badge variant="outline" className="text-xs">AMM: {selectedEphyProduit.numero_amm}</Badge>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSelectedEphyAmm(selectedEphyProduit.numero_amm);
                      setShowEphyDetail(true);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Voir fiche
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-muted-foreground mb-3">
                Les champs ci-dessous sont pré-remplis depuis EPHY. Vous pouvez les modifier si nécessaire.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom commercial *</Label>
                <Input
                  value={newProduit.nom_commercial}
                  onChange={(e) => setNewProduit(p => ({ ...p, nom_commercial: e.target.value }))}
                  placeholder="Ex: Roundup"
                />
              </div>
              <div className="space-y-2">
                <Label>N° AMM</Label>
                <Input
                  value={newProduit.numero_amm}
                  onChange={(e) => setNewProduit(p => ({ ...p, numero_amm: e.target.value }))}
                  placeholder="Ex: 2000059"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Substance(s) active(s)</Label>
              <Input
                value={newProduit.substance_active}
                onChange={(e) => setNewProduit(p => ({ ...p, substance_active: e.target.value }))}
                placeholder="Ex: Glyphosate"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de produit</Label>
                <Select 
                  value={newProduit.type_produit}
                  onValueChange={(v) => setNewProduit(p => ({ ...p, type_produit: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="herbicide">Herbicide</SelectItem>
                    <SelectItem value="fongicide">Fongicide</SelectItem>
                    <SelectItem value="insecticide">Insecticide</SelectItem>
                    <SelectItem value="adjuvant">Adjuvant</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Formulation</Label>
                <Input
                  value={newProduit.formulation}
                  onChange={(e) => setNewProduit(p => ({ ...p, formulation: e.target.value }))}
                  placeholder="Ex: SC, EC, WG"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Dose max autorisée</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduit.dose_max_ha}
                    onChange={(e) => setNewProduit(p => ({ ...p, dose_max_ha: e.target.value }))}
                    placeholder="0.0"
                    className="flex-1"
                  />
                  <Select 
                    value={newProduit.unite_dose}
                    onValueChange={(v) => setNewProduit(p => ({ ...p, unite_dose: v }))}
                  >
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L/ha">L/ha</SelectItem>
                      <SelectItem value="kg/ha">kg/ha</SelectItem>
                      <SelectItem value="g/ha">g/ha</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>ZNT aquatique (m)</Label>
                <Input
                  type="number"
                  value={newProduit.znt_aquatique}
                  onChange={(e) => setNewProduit(p => ({ ...p, znt_aquatique: e.target.value }))}
                  placeholder="5"
                />
              </div>
              <div className="space-y-2">
                <Label>ZNT riverains (m)</Label>
                <Input
                  type="number"
                  value={newProduit.znt_riverains}
                  onChange={(e) => setNewProduit(p => ({ ...p, znt_riverains: e.target.value }))}
                  placeholder="3"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Délai de réentrée (h)</Label>
                <Input
                  type="number"
                  value={newProduit.delai_reentree}
                  onChange={(e) => setNewProduit(p => ({ ...p, delai_reentree: e.target.value }))}
                  placeholder="6"
                />
              </div>
              <div className="space-y-2">
                <Label>Fabricant</Label>
                <Input
                  value={newProduit.fabricant}
                  onChange={(e) => setNewProduit(p => ({ ...p, fabricant: e.target.value }))}
                  placeholder="Ex: BASF, BAYER"
                />
              </div>
            </div>

            {/* Affichage des phrases de danger si présentes (depuis EPHY) */}
            {newProduit.phrases_danger?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-medium text-red-800 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Phrases de danger (EPHY)
                </p>
                <div className="space-y-1">
                  {newProduit.phrases_danger.slice(0, 4).map((p, idx) => (
                    <p key={idx} className="text-xs text-red-700">
                      <span className="font-mono font-bold">{p.code}</span> - {p.texte}
                    </p>
                  ))}
                  {newProduit.phrases_danger.length > 4 && (
                    <p className="text-xs text-red-600">+ {newProduit.phrases_danger.length - 4} autres...</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stock initial</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newProduit.quantite_stock}
                  onChange={(e) => setNewProduit(p => ({ ...p, quantite_stock: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Date péremption</Label>
                <Input
                  type="date"
                  value={newProduit.date_peremption}
                  onChange={(e) => setNewProduit(p => ({ ...p, date_peremption: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProduit(false)}>
              Annuler
            </Button>
            <Button onClick={createProduit} disabled={!newProduit.nom_commercial}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nouvelle Intervention */}
      <Dialog open={showNewIntervention} onOpenChange={setShowNewIntervention}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sprout className="w-5 h-5 text-green-600" />
              Enregistrer une intervention
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Parcelle *</Label>
                <Select 
                  value={newIntervention.parcelle_id}
                  onValueChange={(v) => {
                    const parc = parcelles.find(p => p.id === v);
                    setNewIntervention(i => ({ 
                      ...i, 
                      parcelle_id: v,
                      surface_traitee: parc?.surface_ha || parc?.surface || ''
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {parcelles.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nom} ({p.surface_ha || p.surface} ha)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Produit *</Label>
                <Select 
                  value={newIntervention.produit_id}
                  onValueChange={(v) => {
                    const prod = produits.find(p => p.id === v);
                    setNewIntervention(i => ({ 
                      ...i, 
                      produit_id: v,
                      unite_dose: prod?.unite_dose || 'L/ha'
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {produits.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nom_commercial} ({p.type_produit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newIntervention.date_intervention}
                  onChange={(e) => setNewIntervention(i => ({ ...i, date_intervention: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Surface traitée (ha)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newIntervention.surface_traitee}
                  onChange={(e) => setNewIntervention(i => ({ ...i, surface_traitee: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Dose appliquée *</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  value={newIntervention.dose_appliquee}
                  onChange={(e) => setNewIntervention(i => ({ ...i, dose_appliquee: e.target.value }))}
                  placeholder="0.0"
                  className="flex-1"
                />
                <Select 
                  value={newIntervention.unite_dose}
                  onValueChange={(v) => setNewIntervention(i => ({ ...i, unite_dose: v }))}
                >
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L/ha">L/ha</SelectItem>
                    <SelectItem value="kg/ha">kg/ha</SelectItem>
                    <SelectItem value="g/ha">g/ha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cible (adventice, maladie, ravageur)</Label>
                <CibleSuggestInput
                  value={newIntervention.cible}
                  onChange={(val) => setNewIntervention(i => ({ ...i, cible: val }))}
                  placeholder="Tapez pour voir les suggestions..."
                />
              </div>
              <div className="space-y-2">
                <Label>Stade culture</Label>
                <Input
                  value={newIntervention.stade_culture}
                  onChange={(e) => setNewIntervention(i => ({ ...i, stade_culture: e.target.value }))}
                  placeholder="Ex: BBCH 32"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Température (°C)</Label>
                <Input
                  type="number"
                  value={newIntervention.temperature}
                  onChange={(e) => setNewIntervention(i => ({ ...i, temperature: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Vent (km/h)</Label>
                <Input
                  type="number"
                  value={newIntervention.vent_kmh}
                  onChange={(e) => setNewIntervention(i => ({ ...i, vent_kmh: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Hygrométrie (%)</Label>
                <Input
                  type="number"
                  value={newIntervention.hygrometrie}
                  onChange={(e) => setNewIntervention(i => ({ ...i, hygrometrie: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Opérateur</Label>
                <Input
                  value={newIntervention.operateur_nom}
                  onChange={(e) => setNewIntervention(i => ({ ...i, operateur_nom: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Matériel</Label>
                <Input
                  value={newIntervention.materiel_nom}
                  onChange={(e) => setNewIntervention(i => ({ ...i, materiel_nom: e.target.value }))}
                  placeholder="Ex: Pulvérisateur 800L"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Observations</Label>
              <Input
                value={newIntervention.notes}
                onChange={(e) => setNewIntervention(i => ({ ...i, notes: e.target.value }))}
                placeholder="Notes, conditions particulières..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewIntervention(false)}>
              Annuler
            </Button>
            <Button 
              onClick={createIntervention} 
              disabled={!newIntervention.parcelle_id || !newIntervention.produit_id || !newIntervention.dose_appliquee}
            >
              <Plus className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Export */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Exporter le registre
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Exporter le registre phytosanitaire de la campagne {campagne} au format JSON.
            </p>
            <p className="text-sm">
              Contient {interventions.length} interventions
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExport(false)}>
              Annuler
            </Button>
            <Button onClick={exportRegistre}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrePhytoPage;
